import type {
  CreateFormFieldInput,
  CreateFormInput,
  CreateFormSubmissionInput,
  CreateFormTagMappingInput,
  DeleteFormSubmissionOutput,
  DeleteFormTagMappingOutput,
  Form,
  FormField,
  FormSubmission,
  FormTagMapping,
  ReorderFieldOptionsInput,
  ReorderFieldOptionsOutput,
  ReorderFormFieldsInput,
  ReorderFormFieldsOutput,
  ReviewFormSubmissionInput,
  SubmissionResponsesOutput,
  UpdateFormFieldInput,
  UpdateFormInput,
} from '@sunrise-ui/api/insights';
import { useSnackbar } from '@sunrise-ui/primitives';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { QueryKeys } from '../constants/QueryKeys';
import { formsApi, useAuthMutation, useAuthQuery } from '../utils/api';
import {
  assertParameterIsNotEmptyString,
  assertParameterIsNotUndefinedOrNull,
} from '../utils/error';
import { transformFormFields } from '../utils/formFieldTransform';

const {
  formsKey,
  byCommunityKey,
  byIdKey,
  bySlugKey,
  fieldsKey,
  submissionsKey,
  responsesKey,
  bulkKey,
  tagMappingsKey,
} = QueryKeys.forms;

// ============================================================================
// Form Queries
// ============================================================================

export const useGetForms = (communityId?: string, publishedOnly?: boolean) => {
  const queryClient = useQueryClient();

  return useAuthQuery({
    queryFn: async () => {
      assertParameterIsNotEmptyString(communityId, 'communityId');
      const response = await formsApi.listFormsFormsCommunitiesCommunityIdGet({
        communityId: communityId!,
        publishedOnly,
      });

      // Cache each form individually
      for (const form of response.data) {
        queryClient.setQueryData([formsKey, byIdKey, form.id], form);
      }

      return response.data;
    },
    queryKey: [formsKey, byCommunityKey, communityId, publishedOnly],
    enabled: Boolean(communityId),
  });
};

export const useGetFormById = (formId?: string) => {
  return useAuthQuery({
    queryFn: async () => {
      assertParameterIsNotEmptyString(formId, 'formId');
      const response = await formsApi.getFormFormsFormIdGet({
        formId: formId!,
      });
      return response.data;
    },
    queryKey: [formsKey, byIdKey, formId],
    enabled: Boolean(formId),
  });
};

export const useGetFormBySlug = (slug?: string) => {
  return useAuthQuery({
    queryFn: async () => {
      assertParameterIsNotEmptyString(slug, 'slug');
      const response = await formsApi.getFormBySlugFormsSlugSlugGet({
        slug: slug!,
      });
      return response.data;
    },
    queryKey: [formsKey, bySlugKey, slug],
    enabled: Boolean(slug),
  });
};

// ============================================================================
// Form Mutations
// ============================================================================

export const useCreateForm = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useAuthMutation<Form, unknown, { createFormInput: CreateFormInput }>({
    async mutationFn({ createFormInput }) {
      const response = await formsApi.createFormFormsPost({
        createFormInput,
      });
      return response.data;
    },
    async onSuccess(form) {
      const { community_id } = form;
      await queryClient.invalidateQueries({
        queryKey: [formsKey, byCommunityKey, community_id],
      });

      showSnackbar({
        text: 'Form created successfully!',
        type: 'success',
      });
    },
    async onError() {
      showSnackbar({
        text: 'Failed to create form. Please try again.',
        type: 'error',
      });
    },
  });
};

export const useUpdateForm = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useAuthMutation<
    Form,
    unknown,
    { formId: string; updateFormInput: UpdateFormInput }
  >({
    async mutationFn({ formId, updateFormInput }) {
      assertParameterIsNotEmptyString(formId, 'formId');
      const response = await formsApi.updateFormFormsFormIdPut({
        formId,
        updateFormInput,
      });
      return response.data;
    },
    async onSuccess(form) {
      const { community_id, id } = form;
      await queryClient.invalidateQueries({
        queryKey: [formsKey, byCommunityKey, community_id],
      });
      await queryClient.invalidateQueries({
        queryKey: [formsKey, byIdKey, id],
      });

      showSnackbar({
        text: 'Form updated successfully!',
        type: 'success',
      });
    },
    async onError() {
      showSnackbar({
        text: 'Failed to update form. Please try again.',
        type: 'error',
      });
    },
  });
};

export const useDeleteForm = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useAuthMutation<
    void,
    unknown,
    { formId: string; communityId: string }
  >({
    async mutationFn({ formId }) {
      assertParameterIsNotEmptyString(formId, 'formId');
      await formsApi.deleteFormFormsFormIdDelete({ formId });
    },
    async onSuccess(_, { communityId, formId }) {
      await queryClient.invalidateQueries({
        queryKey: [formsKey, byCommunityKey, communityId],
      });
      queryClient.removeQueries({
        queryKey: [formsKey, byIdKey, formId],
      });

      showSnackbar({
        text: 'Form deleted successfully!',
        type: 'success',
      });
    },
    async onError() {
      showSnackbar({
        text: 'Failed to delete form. Please try again.',
        type: 'error',
      });
    },
  });
};

// ============================================================================
// Form Field Queries & Mutations
// ============================================================================

export const useGetFormFields = (formId?: string) => {
  return useAuthQuery({
    queryFn: async () => {
      assertParameterIsNotUndefinedOrNull(formId, 'formId');
      const response = await formsApi.listFormFieldsFormsFormIdFieldsGet({
        formId,
      });
      // Transform API fields to app-friendly render configs
      return transformFormFields(response.data);
    },
    queryKey: [formsKey, fieldsKey, formId],
    enabled: Boolean(formId),
  });
};

export const useCreateFormField = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useAuthMutation<
    FormField,
    unknown,
    { createFormFieldInput: CreateFormFieldInput }
  >({
    async mutationFn({ createFormFieldInput }) {
      const response = await formsApi.createFormFieldFormsFieldsPost({
        createFormFieldInput,
      });
      return response.data;
    },
    async onSuccess(field) {
      await queryClient.invalidateQueries({
        queryKey: [formsKey, fieldsKey, field.form_id],
      });
    },
    async onError() {
      showSnackbar({
        text: 'Failed to create field. Please try again.',
        type: 'error',
      });
    },
  });
};

export const useUpdateFormField = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useAuthMutation<
    FormField,
    unknown,
    { fieldId: string; updateFormFieldInput: UpdateFormFieldInput }
  >({
    async mutationFn({ fieldId, updateFormFieldInput }) {
      assertParameterIsNotEmptyString(fieldId, 'fieldId');
      const response = await formsApi.updateFormFieldFormsFieldsFieldIdPut({
        fieldId,
        updateFormFieldInput,
      });
      return response.data;
    },
    async onSuccess(field) {
      await queryClient.invalidateQueries({
        queryKey: [formsKey, fieldsKey, field.form_id],
      });
    },
    async onError() {
      showSnackbar({
        text: 'Failed to update field. Please try again.',
        type: 'error',
      });
    },
  });
};

export const useDeleteFormField = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useAuthMutation<void, unknown, { fieldId: string; formId: string }>({
    async mutationFn({ fieldId }) {
      assertParameterIsNotEmptyString(fieldId, 'fieldId');
      await formsApi.deleteFormFieldFormsFieldsFieldIdDelete({ fieldId });
    },
    async onSuccess(_, { formId }) {
      await queryClient.invalidateQueries({
        queryKey: [formsKey, fieldsKey, formId],
      });
    },
    async onError() {
      showSnackbar({
        text: 'Failed to delete field. Please try again.',
        type: 'error',
      });
    },
  });
};

export const useReorderFormFields = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useAuthMutation<
    ReorderFormFieldsOutput,
    unknown,
    { formId: string; reorderFormFieldsInput: ReorderFormFieldsInput }
  >({
    async mutationFn({ formId, reorderFormFieldsInput }) {
      assertParameterIsNotEmptyString(formId, 'formId');
      const response =
        await formsApi.reorderFormFieldsFormsFormIdFieldsReorderPost({
          formId,
          reorderFormFieldsInput,
        });
      return response.data;
    },
    async onSuccess(_, { formId }) {
      await queryClient.invalidateQueries({
        queryKey: [formsKey, fieldsKey, formId],
      });
    },
    async onError() {
      showSnackbar({
        text: 'Failed to reorder fields. Please try again.',
        type: 'error',
      });
    },
  });
};

export const useReorderFieldOptions = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useAuthMutation<
    ReorderFieldOptionsOutput,
    unknown,
    {
      fieldId: string;
      formId: string;
      reorderFieldOptionsInput: ReorderFieldOptionsInput;
    }
  >({
    async mutationFn({ fieldId, reorderFieldOptionsInput }) {
      assertParameterIsNotEmptyString(fieldId, 'fieldId');
      const response =
        await formsApi.reorderFieldOptionsFormsFieldsFieldIdOptionsReorderPost({
          fieldId,
          reorderFieldOptionsInput,
        });
      return response.data;
    },
    async onSuccess(_, { formId }) {
      await queryClient.invalidateQueries({
        queryKey: [formsKey, fieldsKey, formId],
      });
    },
    async onError() {
      showSnackbar({
        text: 'Failed to reorder options. Please try again.',
        type: 'error',
      });
    },
  });
};

// ============================================================================
// Form Submission Queries & Mutations
// ============================================================================

export const useGetFormSubmissions = (formId?: string) => {
  return useAuthQuery({
    queryFn: async () => {
      assertParameterIsNotEmptyString(formId, 'formId');
      const response =
        await formsApi.listFormSubmissionsFormsFormIdSubmissionsGet({
          formId: formId!,
        });
      return response.data;
    },
    queryKey: [formsKey, submissionsKey, formId],
    enabled: Boolean(formId),
  });
};

export const useGetFormSubmission = (submissionId?: string) => {
  return useAuthQuery({
    queryFn: async () => {
      assertParameterIsNotUndefinedOrNull(submissionId, 'submissionId');
      const response =
        await formsApi.getFormSubmissionFormsSubmissionsSubmissionIdGet({
          submissionId,
        });
      return response.data;
    },
    queryKey: [formsKey, submissionsKey, 'detail', submissionId],
    enabled: Boolean(submissionId),
  });
};

export const useCreateFormSubmission = ({
  showSnackbars = true,
}: {
  showSnackbars?: boolean;
} = {}) => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useMutation<
    FormSubmission,
    unknown,
    { createFormSubmissionInput: CreateFormSubmissionInput }
  >({
    async mutationFn({ createFormSubmissionInput }) {
      const response = await formsApi.createFormSubmissionFormsSubmissionsPost({
        createFormSubmissionInput,
      });
      return response.data;
    },
    async onSuccess(submission) {
      await queryClient.invalidateQueries({
        queryKey: [formsKey, submissionsKey, submission.form_id],
      });

      if (showSnackbars) {
        showSnackbar({
          text: 'Form submitted successfully!',
          type: 'success',
        });
      }
    },
    async onError() {
      if (showSnackbars) {
        showSnackbar({
          text: 'Failed to submit form. Please try again.',
          type: 'error',
        });
      }
    },
  });
};

export const useDeleteFormSubmission = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useAuthMutation<
    DeleteFormSubmissionOutput,
    unknown,
    { submissionId: string; formId: string }
  >({
    async mutationFn({ submissionId }) {
      assertParameterIsNotEmptyString(submissionId, 'submissionId');
      const response =
        await formsApi.deleteFormSubmissionFormsSubmissionsSubmissionIdDelete({
          submissionId,
        });
      return response.data;
    },
    async onSuccess(_, { formId }) {
      await queryClient.invalidateQueries({
        queryKey: [formsKey, submissionsKey, formId],
      });

      showSnackbar({
        text: 'Submission deleted successfully!',
        type: 'success',
      });
    },
    async onError() {
      showSnackbar({
        text: 'Failed to delete submission. Please try again.',
        type: 'error',
      });
    },
  });
};

export const useReviewFormSubmission = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useAuthMutation<
    FormSubmission,
    unknown,
    {
      submissionId: string;
      formId: string;
      reviewFormSubmissionInput: ReviewFormSubmissionInput;
    }
  >({
    async mutationFn({ submissionId, reviewFormSubmissionInput }) {
      assertParameterIsNotEmptyString(submissionId, 'submissionId');
      const response =
        await formsApi.reviewFormSubmissionFormsSubmissionsSubmissionIdReviewPatch(
          {
            submissionId,
            reviewFormSubmissionInput,
          },
        );
      return response.data;
    },
    async onSuccess(_, { formId, submissionId }) {
      await queryClient.invalidateQueries({
        queryKey: [formsKey, submissionsKey, formId],
      });
      await queryClient.invalidateQueries({
        queryKey: [formsKey, submissionsKey, 'detail', submissionId],
      });

      showSnackbar({
        text: 'Submission reviewed!',
        type: 'success',
      });
    },
    async onError() {
      showSnackbar({
        text: 'Failed to review submission. Please try again.',
        type: 'error',
      });
    },
  });
};

// ============================================================================
// Submission Responses Queries
// ============================================================================

export const useGetSubmissionResponses = (submissionId?: string) => {
  return useAuthQuery<SubmissionResponsesOutput>({
    queryFn: async () => {
      assertParameterIsNotEmptyString(submissionId, 'submissionId');
      const response =
        await formsApi.getSubmissionResponsesFormsSubmissionsSubmissionIdResponsesGet(
          {
            submissionId: submissionId!,
          },
        );
      return response.data;
    },
    queryKey: [formsKey, submissionsKey, responsesKey, submissionId],
    enabled: Boolean(submissionId),
  });
};

export const useBulkGetSubmissionResponses = (submissionIds?: string[]) => {
  return useAuthQuery<SubmissionResponsesOutput[]>({
    queryFn: async () => {
      const response =
        await formsApi.bulkGetSubmissionResponsesFormsSubmissionsResponsesBulkPost(
          {
            bulkGetSubmissionResponsesInput: {
              submission_ids: submissionIds!,
            },
          },
        );
      return response.data;
    },
    queryKey: [formsKey, submissionsKey, responsesKey, bulkKey, submissionIds],
    enabled: Boolean(submissionIds && submissionIds.length > 0),
  });
};

// =========================================================================
// Form Tag Mappings
// =========================================================================

export const useGetFormTagMappings = (formId?: string) => {
  return useAuthQuery<FormTagMapping[]>({
    queryFn: async () => {
      assertParameterIsNotEmptyString(formId, 'formId');
      const response =
        await formsApi.listFormTagMappingsFormsFormIdTagMappingsGet({
          formId: formId!,
        });
      return response.data;
    },
    queryKey: [formsKey, tagMappingsKey, formId],
    enabled: Boolean(formId),
  });
};

export const useCreateFormTagMapping = (options?: {
  showErrorSnackbar?: boolean;
}) => {
  const { showErrorSnackbar = true } = options ?? {};
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useAuthMutation<
    FormTagMapping,
    unknown,
    { createFormTagMappingInput: CreateFormTagMappingInput }
  >({
    async mutationFn({ createFormTagMappingInput }) {
      const response = await formsApi.createFormTagMappingFormsTagMappingsPost({
        createFormTagMappingInput,
      });
      return response.data;
    },
    async onSuccess(mapping) {
      await queryClient.invalidateQueries({
        queryKey: [formsKey, tagMappingsKey, mapping.form_id],
      });
    },
    async onError() {
      if (showErrorSnackbar) {
        showSnackbar({
          text: 'Failed to create tag mapping. Please try again.',
          type: 'error',
        });
      }
    },
  });
};

export const useDeleteFormTagMapping = (options?: {
  showErrorSnackbar?: boolean;
}) => {
  const { showErrorSnackbar = true } = options ?? {};
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useAuthMutation<
    DeleteFormTagMappingOutput,
    unknown,
    { mappingId: string; formId: string }
  >({
    async mutationFn({ mappingId }) {
      assertParameterIsNotEmptyString(mappingId, 'mappingId');
      const response =
        await formsApi.deleteFormTagMappingFormsTagMappingsMappingIdDelete({
          mappingId,
        });
      return response.data;
    },
    async onSuccess(_, { formId }) {
      await queryClient.invalidateQueries({
        queryKey: [formsKey, tagMappingsKey, formId],
      });
    },
    async onError() {
      if (showErrorSnackbar) {
        showSnackbar({
          text: 'Failed to delete tag mapping. Please try again.',
          type: 'error',
        });
      }
    },
  });
};
